<template>
  <div
    v-click-outside="cancelEditing"
    :class="{ completed, editing, overdue, duetoday }"
    class="task ui form item clearing"
  >
    <!-- .row-hidden mirrors the v-show: the mobile block below needs
         `display: flex !important` (to out-specify the global native
         column-stacking), which also beats v-show's inline display:none --
         without the class the static row stayed visible behind its own
         open editor on <=768px viewports. -->
    <div
      v-show="!editing && task.id"
      :class="{ 'row-hidden': editing || !task.id }"
      class="content ui grid"
    >
      <div
        class="ui checkbox column eleven wide"
        :class="{ checked: completed }"
      >
        <input
          class="hidden"
          type="checkbox"
          :checked="completed"
          @change="toggleComplete"
        />
        <label>
          <span @click.stop.prevent="startEditing">{{ task.name }}</span>
          <a
            v-if="showEvent"
            :href="task.url"
            class="event"
            @click.stop.prevent="openTask"
          >
            <i class="icon chevron right fitted" />
            {{ task.event.name }}
          </a>
        </label>
      </div>
      <div class="ui column three wide right aligned">
        <span v-if="task.due_on" class="date margin-right-tiny">
          {{ task.due_on | formatDate }}
        </span>

        <a
          :data-tooltip="'Show ' + task.user_login + '\'s tasks'"
          data-inverted
          data-variation="mini"
          @click.stop.prevent="clickAvatar"
        >
          <avatar
            v-if="task.user_login"
            class="ui image avatar"
            :title="task.user_login"
            :username="task.user_login"
            :src="task.user_gravatar"
            size="26"
          />
        </a>
      </div>
      <div class="ui icon buttons tiny compact nub column two wide">
        <a class="ui button" @click="startEditing">
          <i class="icon pencil alternate" />
        </a>
        <a class="ui button" :class="{ loading }" @click="removeTask">
          <i class="icon delete" />
        </a>
      </div>
    </div>

    <task-input
      v-if="editing || !task.id"
      class="content"
      :task="task"
      :users="users"
      :in-place="true"
      :parent-event="task.event"
      @cancelEditing="cancelEditing"
      @removeTask="removeTask"
    />
  </div>
</template>

<script>
import Avatar from "components/avatar.vue";
import ClickOutside from "vue-click-outside";
import TaskInput from "./TaskInput.vue";

const { $, moment } = window;

export default {
  directives: {
    ClickOutside,
  },
  components: {
    TaskInput,
    Avatar,
  },
  props: ["name", "task", "users", "grouping", "parentEvent"],
  data() {
    return {
      loading: false,
      newText: this.task.name,
      // editing: false
    };
  },
  computed: {
    showEvent() {
      return this.grouping != "event" && this.task.event && !this.parentEvent;
    },
    editing() {
      return this.task.editing;
    },
    completed() {
      return this.task.completed_on && this.task.completed_on !== "";
    },
    overdue() {
      return !this.completed && moment(this.task.due_on).isBefore();
    },
    duetoday() {
      return (
        !this.completed && moment(this.task.due_on).isSame(moment(), "day")
      );
    },
  },
  watch: {
    editing(val) {
      this.$emit("update:editing", val);
    },
  },
  mounted() {
    $(this.$el).find(".ui.checkbox").checkbox();
  },
  methods: {
    clickAvatar() {
      this.$emit("selectUser", this.task.user);
    },
    openTask() {
      this.$emit("openEvent", this.task);
    },
    toggleComplete() {
      if (this.task.completed_on) {
        this.$store.dispatch("task/markIncomplete", this.task);
      } else {
        this.$store.dispatch("task/markComplete", this.task);
      }
    },
    // addTask(addedTask) {
    //   this.editing = false;
    //   this.$emit("addTaskInPlace", this.task, addedTask);
    // },
    removeTask() {
      this.loading = true;
      this.$emit("removeTask");
      this.$store.dispatch("task/destroy", this.task);
    },
    startEditing() {
      this.$set(this.task, "editing", true);
      this.$nextTick(() => {
        $(this.$el).find("input.new-task").focus();
      });
    },
    cancelEditing() {
      if (this.task.id) {
        this.$set(this.task, "editing", false);
      }
      this.newText = this.name;
    },
  },
};
</script>

<style lang="scss" scoped>
.item {
  padding-top: 0.5em !important;
}

.nub {
  visibility: hidden;
  /* margin-right: -25px; */
  // margin-left: 5px;
}

.item:hover .nub {
  visibility: visible;
}

.item label {
  word-break: break-all;
  display: block;
  line-height: 1.2;
  transition: color 0.4s;

  a.event {
    margin-left: 1em;
    font-size: 0.9em;
    color: var(--text-muted);
  }

  &:before,
  &:after {
    margin-top: -2px;
  }
}
.item.completed label,
.item.completed .ui.right.floated * {
  /*opacity: 0.5 !important;*/
}
.item.completed label,
.item.completed .ui.label {
  text-decoration: line-through;
}

.item.overdue {
  .date {
    color: rgb(235, 92, 107);
  }
}

.item.duetoday {
  .date {
    color: green;
  }
}

@media (max-width: 768px) {
  // The three Fomantic grid columns (checkbox+text / due-date+avatar /
  // edit+delete) were stacking full-width (native column-stacking), which
  // pushed the actions into their own row below a dead band -- one 52px
  // row instead (spec §13.5).
  .content.ui.grid {
    display: flex !important;
    flex-wrap: nowrap;
    align-items: center;
    min-height: 52px;
    margin: 0 !important;
  }

  // Re-assert the v-show contract over the !important above (see template).
  .content.ui.grid.row-hidden {
    display: none !important;
  }

  // The global native column-stacking rule
  // (`.ui.grid:not(.unstackable) > .wide.column { width: 100% !important }`
  // in hotwire_native.scss) ties this component's scoped specificity, and
  // wins the tie by source order -- repeating each column's full class
  // list (a standard CSS specificity-bump idiom, not a typo) is what
  // actually out-specifies it. Without this the three columns each
  // rendered at the *grid's* full width instead of sharing one row, and
  // the squeezed checkbox+text column wrapped one character per line.
  .content.ui.grid > .ui.checkbox.column.eleven.wide {
    width: auto !important;
    flex: 1 1 0 !important;
    min-width: 0 !important;
    padding: 6px 4px !important;

    label {
      font-size: 17px;
    }
  }

  .content.ui.grid > .ui.column.three.wide.right.aligned {
    width: auto !important;
    flex: 0 0 auto !important;
    display: flex !important;
    align-items: center;
    padding: 6px 4px !important;

    .date {
      font-size: 12px;
    }
  }

  // Touch has no hover: keep the edit/delete actions visible and tappable,
  // right-aligned at natural size (the native column stacking would
  // otherwise stretch the pair across the full row).
  .content.ui.grid > .ui.icon.buttons.tiny.compact.nub.column.two.wide {
    width: auto !important;
    flex: 0 0 auto !important;
    padding: 6px 4px !important;
    visibility: visible;
    display: flex !important;
    justify-content: flex-end;
    align-items: center;

    .ui.button {
      // The column's `two wide` classes double as `.ui.two.buttons > .button`
      // 50%-width sizing; keep the pair at natural icon-button size.
      flex: 0 0 auto;
      width: auto !important;
      min-width: 44px;
      min-height: 44px;
    }
  }

  // The checkbox row is the task's primary target; give it a full-height
  // touch area instead of a 31px strip.
  .content .ui.checkbox {
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  // Native (assessment: Tasks #1's "gray hover-action slab"): desktop
  // hover-actions made permanently visible arrived as a solid Fomantic
  // button box bolted to the row. Quiet them to borderless glyphs, and
  // drop the pencil entirely -- tapping the task text already edits
  // (startEditing on the label span), so delete is the only affordance
  // that needs a visible home. More room for the text = no forced wrap.
  html[data-hotwire-native]
    .content.ui.grid
    > .ui.icon.buttons.tiny.compact.nub.column.two.wide {
    .ui.button {
      background: transparent !important;
      box-shadow: none !important;
      color: var(--text-muted, #787a92);
    }

    > a.ui.button:first-of-type {
      display: none;
    }
  }

  // Native: `break-all` breaks mid-word ("Playw / right") whenever a task
  // wraps -- a web tell. Wrap at word boundaries, breaking long unbroken
  // strings (URLs) only as a last resort.
  html[data-hotwire-native] .item label {
    word-break: normal;
    overflow-wrap: anywhere;
  }
}
</style>
